import type { TFunction } from 'i18next';

import type { SupportedLanguage } from '../../i18n';

import { formatDecimal } from './formatters';
import type { InterpretationEntry } from './timeline-model';

/** The verb of an opening per side; a side the channel did not give reads "open". */
const OPEN_VERB = {
  BUY: 'positions.understood.buy',
  SELL: 'positions.understood.sell',
  UNKNOWN: 'positions.understood.openUnknownSide',
} as const;

/**
 * Builds the "Understood: …" sentence of one interpretation from its structured fields, with the
 * `positions.understood.*` templates (spec R7, I12). The API never sends interpretation text.
 *
 * The sentence is made of short translated parts: "buy XAUUSD at market, SL 3325, TP 3360"
 * (mockup 7). Both languages put the parts in the same order, so joining them is safe.
 *
 * An opening always reads "at market": v1 opens at market whatever price the channel announced
 * (FXM-03 §4; mockup 7 reads "at market" for "BUY GOLD 3340"). `INTERPRETATION.entryPrice` is
 * therefore not shown: the announced price is already on the card, and what that field means is
 * a question for C1 (spec §5 "Requested from C1").
 *
 * The actions are the FXM-03 §4 categories of C1's `InstructionActionSchema` (C1 §5.2,
 * provisional until Q13). `REMOVE_LEVELS` does not say which level went (the interpretation's
 * `stopLoss` and `takeProfit` are null either way), so it reads "stop loss or take profit
 * removed", provisional until the contract point of C1 §8 "Contract points not settled" is settled
 * (split the action, or list the removed levels).
 *
 * @example describeInterpretation(entry, t, 'en') // "buy XAUUSD at market, SL 2640.50, TP 2670.00"
 */
export function describeInterpretation(
  entry: InterpretationEntry,
  t: TFunction,
  language: SupportedLanguage,
): string {
  const decimal = (value: string): string => formatDecimal(value, language);
  switch (entry.action) {
    case 'OPEN': {
      const verb = t(OPEN_VERB[entry.side ?? 'UNKNOWN']);
      const head = [verb, entry.symbol, t('positions.understood.atMarket')]
        .filter((part) => part !== null)
        .join(' ');
      const levels = [
        entry.stopLoss === null
          ? null
          : t('positions.understood.stopLoss', { price: decimal(entry.stopLoss) }),
        entry.takeProfit === null
          ? null
          : t('positions.understood.takeProfit', { price: decimal(entry.takeProfit) }),
      ];
      return [head, ...levels].filter((part) => part !== null).join(', ');
    }
    case 'MOVE_SL_TO_BREAK_EVEN':
      return entry.stopLoss === null
        ? t('positions.understood.breakEven')
        : t('positions.understood.breakEvenAt', { price: decimal(entry.stopLoss) });
    case 'MODIFY_LEVELS': {
      const changes = [
        entry.stopLoss === null
          ? null
          : t('positions.understood.stopLossMoved', { price: decimal(entry.stopLoss) }),
        entry.takeProfit === null
          ? null
          : t('positions.understood.takeProfitMoved', { price: decimal(entry.takeProfit) }),
      ].filter((part) => part !== null);
      return changes.length === 0 ? t('positions.understood.modified') : changes.join(', ');
    }
    case 'PARTIAL_CLOSE':
      return entry.closePercent === null
        ? t('positions.understood.partialCloseUnknown')
        : t('positions.understood.partialClose', { percent: decimal(entry.closePercent) });
    case 'REMOVE_LEVELS':
      return t('positions.understood.levelsRemoved');
    case 'CLOSE':
      return t('positions.understood.close');
    case 'CLOSE_ALL':
      // FXM-03 §4: the one message that closes every position of the channel.
      return t('positions.understood.closeAll');
    case 'CANCEL':
      // FXM-03 §4: cancelling a signal just posted is an immediate close of its position.
      return t('positions.understood.cancel');
    case 'IGNORED_AMBIGUOUS':
      // FXM-03 §4: an ambiguous message triggers nothing.
      return t('positions.understood.ambiguous');
    case 'UNKNOWN':
      // An action added by a newer API (C1 R10): a neutral line, never the raw value.
      return t('positions.understood.unknown');
  }
}
